import * as yup from "yup";

const urlValidation = yup
  .string()
  .test("isURL", "正しいURLを入力してください", (value) => {
    try {
      if (!value) return true;
      new URL(value);
      return true;
    } catch {
      return false;
    }
  });

const urlValidationRequired = yup
  .string()
  .required("URLを入力してください")
  .test("isURL", "正しいURLを入力してください", (value) => {
    try {
      if (!value) return true;
      new URL(value);
      return true;
    } catch {
      return false;
    }
  });

const importPageSchema = yup.object({
  url: urlValidation.nullable().default(""),
  mobileUrl: urlValidation.nullable().default(""),
  tabletUrl: urlValidation.nullable().default(""),
  queryMatchType: yup.string().default("EQUAL_MATCH"),
  urlMatchType: yup.string().default("EQUAL_TYPE"),
  isEnableWhiteout: yup.boolean().default(false),
  isSpa: yup.boolean().default(false),
});

const thanksRequiredPageSchema = yup.object({
  url: urlValidationRequired,
  mobileUrl: urlValidation.nullable().default(""),
  tabletUrl: urlValidation.nullable().default(""),
  queryMatchType: yup.string().default("EQUAL_MATCH"),
  urlMatchType: yup.string().required("URLの一致タイプを選択してください"),
  isEnableWhiteout: yup.boolean().default(false),
  isSpa: yup.boolean().default(false),
});

const landingPageSchema = thanksRequiredPageSchema.shape({
  landingUrlFormIncluded: yup.boolean().default(false),
});

const funnelImportRowSchema = yup.object({
  action: yup
    .string()
    .default("")
    .test(
      "funnel-import-action",
      "アクションを選択してください",
      (value): value is "CREATE" | "UPDATE" =>
        value === "CREATE" || value === "UPDATE"
    ),
  funnelId: yup.string().when("action", {
    is: "UPDATE",
    then: (schema) =>
      schema.required("UPDATEの場合はファネルIDを入力してください"),
    otherwise: (schema) => schema.nullable().default(""),
  }),
  name: yup.string().required("ファネル名を入力してください"),
  isExperiment: yup.boolean().default(false),
  landingPage: landingPageSchema.required(),
  formPages: yup.array().of(importPageSchema).default([]),
  confirmPage: importPageSchema.required(),
  thanksPage: thanksRequiredPageSchema.required(),
});

export const validationSchema = yup.object({
  funnels: yup
    .array()
    .of(funnelImportRowSchema)
    .required()
    .min(1, "1件以上のファネルを追加してください"),
});

export type ImportPageValues = {
  url: string;
  mobileUrl: string;
  tabletUrl: string;
  queryMatchType: string;
  urlMatchType: string;
  /** Landing page only — same meaning as funnel create/edit `landingUrlFormIncluded`. */
  landingUrlFormIncluded?: boolean;
  isEnableWhiteout: boolean;
  isSpa: boolean;
};

/** Same pattern as popup import: CSV may omit action until the user selects on the review screen. */
export type FunnelImportRowAction = "" | "CREATE" | "UPDATE";

export type FunnelImportRowValues = {
  action: FunnelImportRowAction;
  funnelId: string;
  name: string;
  isExperiment: boolean;
  landingPage: ImportPageValues;
  formPages: ImportPageValues[];
  confirmPage: ImportPageValues;
  thanksPage: ImportPageValues;
};

export type FunnelImportFormValues = {
  funnels: FunnelImportRowValues[];
};

export const defaultPageValues: ImportPageValues = {
  url: "",
  mobileUrl: "",
  tabletUrl: "",
  queryMatchType: "EQUAL_MATCH",
  urlMatchType: "EQUAL_TYPE",
  isEnableWhiteout: false,
  isSpa: false,
};

export const defaultFunnelRow: FunnelImportRowValues = {
  action: "CREATE",
  funnelId: "",
  name: "",
  isExperiment: false,
  landingPage: { ...defaultPageValues, landingUrlFormIncluded: false },
  // One placeholder row matches FunnelImportGroup (always binds formPages[0])
  formPages: [{ ...defaultPageValues }],
  confirmPage: { ...defaultPageValues },
  thanksPage: { ...defaultPageValues },
};

/** GraphQL `QueryMatch` enum names — same values as FunnelForms / DB */
const QUERY_MATCH_VALUES = new Set([
  "EQUAL_MATCH",
  "INCLUDING_MATCH",
  "PREFIX_MATCH",
]);

/** GraphQL `UrlMatch` enum names — same values as FunnelForms / DB */
const URL_MATCH_VALUES = new Set([
  "EQUAL_TYPE",
  "HAS_ID_TYPE",
  "EQUAL_OR_HAS_ID_TYPE",
  "STARTS_WITH_PREFIX_MATCH",
  "REGEX_MATCH_TYPE",
]);

function normalizeQueryMatchType(raw: string | undefined): string {
  const v = (raw ?? "").trim();
  return QUERY_MATCH_VALUES.has(v) ? v : defaultPageValues.queryMatchType;
}

function normalizeUrlMatchType(raw: string | undefined): string {
  const v = (raw ?? "").trim();
  return URL_MATCH_VALUES.has(v) ? v : defaultPageValues.urlMatchType;
}

/**
 * Ensures page fields match GraphQLEnums option `value`s (DB / funnel create form).
 * Merges partial CSV/state with defaults so selects always resolve.
 */
export function normalizeImportPageValues(
  page: Partial<ImportPageValues & { hasForm?: string }> | null | undefined
): ImportPageValues {
  const { hasForm: _omitLegacyHasForm, ...pageRest } = {
    ...(page ?? {}),
  } as Partial<ImportPageValues> & { hasForm?: string };
  const merged = { ...defaultPageValues, ...pageRest };
  const landingUrlFormIncluded =
    typeof merged.landingUrlFormIncluded === "boolean"
      ? merged.landingUrlFormIncluded
      : undefined;
  return {
    ...merged,
    url: merged.url ?? "",
    mobileUrl: merged.mobileUrl ?? "",
    tabletUrl: merged.tabletUrl ?? "",
    queryMatchType: normalizeQueryMatchType(merged.queryMatchType),
    urlMatchType: normalizeUrlMatchType(merged.urlMatchType),
    isEnableWhiteout: Boolean(merged.isEnableWhiteout),
    isSpa: Boolean(merged.isSpa),
    ...(landingUrlFormIncluded !== undefined ? { landingUrlFormIncluded } : {}),
  };
}

function normalizeLandingPage(
  page: Partial<ImportPageValues & { hasForm?: string }> | null | undefined
): ImportPageValues {
  const raw = page ?? {};
  const { hasForm: legacyHasForm, ...rest } = raw;
  const merged = { ...defaultPageValues, ...rest };
  const landingUrlFormIncluded =
    typeof merged.landingUrlFormIncluded === "boolean"
      ? merged.landingUrlFormIncluded
      : legacyHasForm === "HAS_FORM"
      ? true
      : legacyHasForm === "NO_FORM"
      ? false
      : false;

  return {
    ...normalizeImportPageValues(merged),
    landingUrlFormIncluded,
  };
}

/**
 * Normalizes funnel import rows from CSV/navigation state so Formik values
 * always match dropdown option values (same enum strings as funnel create/edit).
 */
export function normalizeFunnelImportRow(
  row: FunnelImportRowValues
): FunnelImportRowValues {
  const formPages =
    row.formPages && row.formPages.length > 0
      ? row.formPages.map((p) => normalizeImportPageValues(p))
      : [normalizeImportPageValues(undefined)];

  return {
    ...row,
    landingPage: normalizeLandingPage(row.landingPage),
    formPages,
    confirmPage: normalizeImportPageValues(row.confirmPage),
    thanksPage: normalizeImportPageValues(row.thanksPage),
  };
}

export function normalizeFunnelImportFormValues(
  funnels: FunnelImportRowValues[] | undefined
): FunnelImportRowValues[] {
  return (funnels ?? []).map(normalizeFunnelImportRow);
}
